import axios from "axios";

export interface ScalekitLogoutConfig {
  envUrl: string;
  clientId: string;
  postLogoutRedirectUri: string;
  idTokenHint?: string;
}

export const buildScalekitLogoutUrl = (config: ScalekitLogoutConfig): string => {
  const params = new URLSearchParams({
    client_id: config.clientId,
    post_logout_redirect_uri: config.postLogoutRedirectUri,
  });
  if (config.idTokenHint) {
    params.set("id_token_hint", config.idTokenHint);
  }
  return `${config.envUrl}/oauth/end_session?${params.toString()}`;
};

export interface ScalekitAuthorizeConfig {
  envUrl: string;
  clientId: string;
  redirectUri: string;
  /** Defaults to "code". */
  responseType?: string;
  /** Defaults to "openid profile email offline_access". */
  scope?: string;
  /** Opaque value Scalekit returns unchanged on the redirect_uri callback. */
  state?: string;
}

/**
 * Hint to tell Scalekit which IdP connection to route the user to. Exactly
 * one of these is typically provided; multiple can be passed if Scalekit is
 * configured to accept them. The mapping to OAuth param names:
 *   loginHint          → login_hint           (typically an email address)
 *   organizationId     → organization_id      (Scalekit org id, e.g. org_...)
 *   idpInitiatedLogin  → idp_initiated_login  (token from an IdP-initiated flow)
 */
export interface ScalekitAuthorizeHint {
  loginHint?: string;
  organizationId?: string;
  idpInitiatedLogin?: string;
}

export const buildScalekitAuthorizeUrl = (
  config: ScalekitAuthorizeConfig,
  hint: ScalekitAuthorizeHint = {}
): string => {
  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: config.responseType ?? "code",
    scope: config.scope ?? "openid profile email offline_access",
  });
  if (config.state) params.set("state", config.state);
  if (hint.loginHint) params.set("login_hint", hint.loginHint);
  if (hint.organizationId) params.set("organization_id", hint.organizationId);
  if (hint.idpInitiatedLogin)
    params.set("idp_initiated_login", hint.idpInitiatedLogin);
  return `${config.envUrl}/oauth/authorize?${params.toString()}`;
};

export interface ExchangeScalekitCodeConfig {
  /** Base URL of the Libertify backend (e.g. https://api.dev.libertify.com). */
  apiUrl: string;
  /** Auth code returned by Scalekit on the redirect_uri callback. */
  code: string;
}

export interface ScalekitExchangeResponse {
  access_token: string;
  token_type: string;
  refresh_token?: string;
  id_token?: string;
}

/**
 * Exchange a Scalekit auth `code` for tokens via the Libertify backend's
 * `/api/tokens/sso` endpoint. Backend talks to Scalekit on the consumer's
 * behalf (so the client_secret never reaches the browser).
 *
 * Returns the raw response intersected with `{ provider: "scalekit" }` so
 * callers don't have to remember to tag the token before passing it to
 * `setToken` or Refine's login mutation. The discriminator is part of the
 * type — so consumers can branch on `provider` with full type narrowing,
 * and a missing tag can't silently break the SLO branch on the next logout.
 */
export const exchangeScalekitCode = async (
  config: ExchangeScalekitCodeConfig
): Promise<ScalekitExchangeResponse & { provider: "scalekit" }> => {
  const response = await axios.post<ScalekitExchangeResponse>(
    `${config.apiUrl}/api/tokens/sso`,
    { transfer_token: config.code }
  );
  return { ...response.data, provider: "scalekit" };
};
